import {useState} from 'react';
import {
  Button,
  Flex,
  FlexProps,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useDisclosure,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';

import {CaptainIcon, ViceCaptainIcon} from '../components/modals/player-stats/icons';

import {useLineupStore} from './useLineupStore';
import {ChangeRoleButton} from '../components/modals';
import {PlayerInfo} from './PlayerItem';
import type {Player} from '../types';

type Role = 'captain' | 'vice-captain';

export function RoleSelectionActions() {
  const [selectedRole, setSelectedRole] = useState<Role>('captain');
  const roleSelectionDisclosure = useDisclosure();

  const t = useTranslate();
  const isCaptainSelected = useLineupStore((store) =>
    store.players
      .flat()
      .concat(store.substitutes)
      .some((player) => player?.is_captain),
  );
  const isViceCaptainSelected = useLineupStore((store) =>
    store.players
      .flat()
      .concat(store.substitutes)
      .some((player) => player?.is_vice_captain),
  );
  const isActionDisabled = useLineupStore((store) => store.numberOfPlayers !== 15);

  const onRoleClick = (role: Role) => () => {
    setSelectedRole(role);
    roleSelectionDisclosure.onOpen();
  };

  return (
    <Flex gap="2" flexDir={['column', 'row']}>
      <ChangeRoleButton
        w="full"
        flexGrow="1"
        title={`${t('app.actions.select')} ${t('fantasy.lineup.player.actions.captain')}`}
        icon={CaptainIcon}
        isActive={isCaptainSelected}
        isDisabled={isActionDisabled}
        onClick={onRoleClick('captain')}
      />

      <ChangeRoleButton
        w="full"
        flexGrow="1"
        title={`${t('app.actions.select')} ${t('fantasy.lineup.player.actions.vice-captain')}`}
        icon={ViceCaptainIcon}
        isActive={isViceCaptainSelected}
        isDisabled={isActionDisabled}
        onClick={onRoleClick('vice-captain')}
      />

      <RoleSelectionModal role={selectedRole} {...roleSelectionDisclosure} />
    </Flex>
  );
}

interface RoleSelectionModalProps extends Omit<ModalProps, 'children'> {
  role: Role;
}

export function RoleSelectionModal({role, ...props}: RoleSelectionModalProps) {
  const t = useTranslate();
  const players = useLineupStore((store) => store.players);
  const substitutes = useLineupStore((store) => store.substitutes);
  const selectCaptain = useLineupStore((store) => store.selectCaptain);
  const selectViceCaptain = useLineupStore((store) => store.selectViceCaptain);

  const onRoleSelect = (player: Player, isSubstitute: boolean) => {
    if (role === 'captain') selectCaptain(player, isSubstitute);
    else selectViceCaptain(player, isSubstitute);

    props.onClose();
  };

  return (
    <Modal size="xl" variant="full" motionPreset="slideInBottom" {...props}>
      <ModalOverlay />

      <ModalContent pt="5" pb="7" px={['4', '6']}>
        <ModalHeader pos="relative" px="0">
          <Flex align="center" justify="space-between" mb="4">
            <Heading as="h3" size="md" fontWeight="800" flex={1}>
              {t('app.actions.select')} {t(`fantasy.lineup.player.actions.${role}`)}
            </Heading>
          </Flex>

          <ModalCloseButton size="lg" />
        </ModalHeader>

        <ModalBody display="flex" flexDir="column" px="0" gap="8">
          <Flex flexDir="column" gap="4">
            <Heading as="h4" textTransform="capitalize">
              {t('fantasy.lineup.players')}
            </Heading>

            {players
              .flat()
              .map(
                (player) =>
                  player && (
                    <PlayerItem
                      key={player.id}
                      player={player}
                      role={role}
                      onClick={() => onRoleSelect(player, false)}
                    />
                  ),
              )}
          </Flex>

          <Flex flexDir="column" gap="4">
            <Heading as="h4" textTransform="capitalize">
              {t('fantasy.lineup.substitutes')}
            </Heading>

            {substitutes.map(
              (player) =>
                player && (
                  <PlayerItem key={player.id} player={player} role={role} onClick={() => onRoleSelect(player, true)} />
                ),
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

interface PlayerItemProps extends FlexProps {
  role: Role;
  player: Player;
}

const PlayerItem = ({player, role, ...props}: PlayerItemProps) => {
  const t = useTranslate();

  const isCaptainSelected = role === 'captain' && !!player.is_captain;
  const isViceCaptainSelected = role === 'vice-captain' && !!player.is_vice_captain;
  const hasRole = isCaptainSelected || isViceCaptainSelected;

  return (
    <Flex
      gap={['3', '4']}
      w="full"
      p="3"
      borderWidth="1px"
      borderColor={hasRole ? 'green.400' : 'gray.200'}
      outline="1px solid"
      outlineColor={hasRole ? 'green.400' : 'transparent'}
      rounded="md"
      transition="border-color 0.3s"
      cursor="pointer"
      {...props}
    >
      <PlayerInfo gap="2" flex="1" inLineup player={player} />

      <Button
        title={`${t('app.actions.select')} ${t(`fantasy.lineup.player.actions.${role}`)}`}
        variant="ghost"
        display={['none', null, 'flex']}
        px={['3', '3.5']}
        fontSize={['xs', 'sm']}
        fontWeight="600"
        color="gray.800"
        borderColor="gray.800"
        borderWidth="1px"
        outline="1px"
        rounded="0.5rem"
        _active={{borderColor: 'green.400'}}
        transition="background-color 0.3s, border-color 0.3s, color 0.3s"
        isDisabled={hasRole}
      >
        {t('app.actions.select')}
      </Button>
    </Flex>
  );
};
